import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_Viewport() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #4ade80, #16a34a)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          🌍 RWD Viewport
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          The viewport is the visible area of a web page on a device. Setting
          the viewport correctly is essential for responsive design.
        </p>
        <img
          src="https://www.w3schools.com/css/img_viewport.png"
          alt="Viewport Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        By default, mobile browsers render pages at a "virtual" desktop width
        and then shrink them to fit the screen. This makes text and layouts
        small and hard to read.
        <br />
        To fix this, we use the <code>&lt;meta viewport&gt;</code> tag, which
        tells the browser how to control the page's dimensions and scaling.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #bbf7d0, #86efac)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            The viewport defines the <b>visible area</b> of a web page.
          </li>
          <li>
            Without a viewport meta tag, mobile devices simulate a desktop
            screen and shrink content.
          </li>
          <li>
            Use{" "}
            <code>
              &lt;meta name="viewport" content="width=device-width,
              initial-scale=1.0"&gt;
            </code>{" "}
            to make pages responsive.
          </li>
          <li>
            <code>width=device-width</code> matches the screen’s actual width.
          </li>
          <li>
            <code>initial-scale=1.0</code> sets the zoom level when the page
            loads.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Meta Viewport */}
      <h2 className="section-title">1️⃣ Basic Meta Viewport</h2>
      <CodeCopyBlock
        code={`<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>`}
      />
      <p>
        ✅ This ensures the layout adapts to the device’s width and starts at
        normal zoom.
      </p>

      {/* Example 2: With User Scaling */}
      <h2 className="section-title">2️⃣ Control User Scaling</h2>
      <CodeCopyBlock
        code={`<meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no">`}
      />
      <p>
        Adding <code>user-scalable=no</code> prevents users from zooming. ⚠️ Not
        recommended for accessibility.
      </p>

      {/* Example 3: Responsive Typography */}
      <h2 className="section-title">3️⃣ Responsive Text with Viewport Units</h2>
      <CodeCopyBlock
        code={`h1 {
  font-size: 5vw; /* 5% of viewport width */
}`}
      />
      <p>
        Using <code>vw</code> (viewport width) or <code>vh</code> (viewport
        height) units makes text scale based on screen size.
      </p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Always include the viewport meta tag for responsive designs.</li>
          <li>
            Combine viewport with <code>@media</code> queries for powerful
            control.
          </li>
          <li>Use viewport units (vw, vh) for fluid text and layouts.</li>
          <li>
            Test across multiple devices to ensure readability and usability.
          </li>
        </ul>
      </div>
    </div>
  );
}
